/**
 * Builds typed EPS create-referral payloads from raw form state.
 *
 * The builder is intentionally pure: it trims strings, coerces money fields to
 * numbers, drops empty optional sections, and returns a discriminated result.
 * It does NOT enforce business validation (see validation.ts) beyond refusing
 * to build from an unknown loan purpose or a completely empty form.
 */
import {
  CASH_OUT_REFINANCE,
  LOAN_PURPOSES,
  emptyBorrowerState,
  type BorrowerFormState,
  type CreatePurchaseReferralRequest,
  type CreateRefinanceReferralRequest,
  type EmploymentInfoRequest,
  type LoanPurpose,
  type MilitaryServiceRequest,
  type ReferralBorrowerRequest,
  type ReferralFormState,
} from "../models/referral";

/**
 * `militaryAnswered` reports whether the application explicitly answered the
 * military question. It is result metadata, not a payload field: nothing is
 * added to the wire by it. Downstream mapping needs it because an asserted
 * `{ isMilitary: true }` looks identical whether it came from an explicit
 * application-level answer or from the legacy form's default-checked control.
 */
export type BuildPayloadResult =
  | {
      ok: true;
      purpose: "purchase";
      request: CreatePurchaseReferralRequest;
      militaryAnswered: boolean;
    }
  | {
      ok: true;
      purpose: "refinance";
      request: CreateRefinanceReferralRequest;
      militaryAnswered: boolean;
    }
  | { ok: false; error: string };

function trimOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/** Parses a money-like string into a number. Returns 0 for blank/invalid. */
export function parseMoney(value: string): number {
  const cleaned = value.replace(/[$,\s]/g, "");
  if (cleaned === "") return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isBorrowerEmpty(b: BorrowerFormState): boolean {
  return Object.values(b).every((v) => v.trim() === "");
}

function buildBorrower(b: BorrowerFormState): ReferralBorrowerRequest {
  const borrower: ReferralBorrowerRequest = {
    firstName: b.firstName.trim(),
    lastName: b.lastName.trim(),
    email: b.email.trim(),
    phone: b.phone.trim(),
  };
  const creditScoreRange = trimOrUndefined(b.creditScoreRange);
  const streetAddress = trimOrUndefined(b.streetAddress);
  const city = trimOrUndefined(b.city);
  const state = trimOrUndefined(b.state);
  const zipCode = trimOrUndefined(b.zipCode);
  if (creditScoreRange) borrower.creditScoreRange = creditScoreRange;
  if (streetAddress) borrower.streetAddress = streetAddress;
  if (city) borrower.city = city;
  if (state) borrower.state = state.toUpperCase();
  if (zipCode) borrower.zipCode = zipCode;
  return borrower;
}

function buildMilitary(state: ReferralFormState): MilitaryServiceRequest | undefined {
  const branch = trimOrUndefined(state.militaryBranch);
  const serviceStatus = trimOrUndefined(state.militaryServiceStatus);
  // An explicitly answered application always carries its answer, including a
  // plain "no", so the wire distinguishes it from an unanswered application.
  if (state.militaryAnswered) {
    const answered: MilitaryServiceRequest = { isMilitary: state.isMilitary };
    if (branch) answered.branch = branch;
    if (serviceStatus) answered.serviceStatus = serviceStatus;
    return answered;
  }
  if (!state.isMilitary && !branch && !serviceStatus) return undefined;
  const military: MilitaryServiceRequest = { isMilitary: state.isMilitary };
  if (branch) military.branch = branch;
  if (serviceStatus) military.serviceStatus = serviceStatus;
  return military;
}

function buildEmployment(state: ReferralFormState): EmploymentInfoRequest | undefined {
  const employmentStatus = trimOrUndefined(state.employmentStatus);
  const incomeRange = trimOrUndefined(state.incomeRange);
  if (!employmentStatus && !incomeRange) return undefined;
  const employment: EmploymentInfoRequest = {};
  if (employmentStatus) employment.employmentStatus = employmentStatus;
  if (incomeRange) employment.incomeRange = incomeRange;
  return employment;
}

function isLoanPurpose(value: string): value is LoanPurpose {
  return (LOAN_PURPOSES as readonly string[]).includes(value);
}

/** True when the user has entered nothing meaningful into the form. */
export function isFormEmpty(state: ReferralFormState): boolean {
  const empty = emptyBorrowerState();
  return (
    isBorrowerEmpty(state.borrower) &&
    isBorrowerEmpty(state.coBorrower) &&
    !state.includeCoBorrower &&
    JSON.stringify(state.coBorrower) === JSON.stringify(empty) &&
    trimOrUndefined(state.partnerReferenceId) === undefined &&
    trimOrUndefined(state.militaryBranch) === undefined &&
    trimOrUndefined(state.militaryServiceStatus) === undefined &&
    trimOrUndefined(state.employmentStatus) === undefined &&
    trimOrUndefined(state.incomeRange) === undefined &&
    trimOrUndefined(state.propertyStreetAddress) === undefined &&
    trimOrUndefined(state.propertyCity) === undefined &&
    trimOrUndefined(state.propertyState) === undefined &&
    trimOrUndefined(state.propertyZipCode) === undefined &&
    trimOrUndefined(state.propertyType) === undefined &&
    trimOrUndefined(state.estimatedValue) === undefined &&
    trimOrUndefined(state.loanAmount) === undefined &&
    trimOrUndefined(state.downPayment) === undefined &&
    trimOrUndefined(state.currentLoanBalance) === undefined &&
    trimOrUndefined(state.refinanceType) === undefined
  );
}

function buildCommon(state: ReferralFormState) {
  const partnerReferenceId = trimOrUndefined(state.partnerReferenceId);
  const propertyCity = trimOrUndefined(state.propertyCity);
  const propertyZipCode = trimOrUndefined(state.propertyZipCode);
  const propertyStreetAddress = trimOrUndefined(state.propertyStreetAddress);
  const coBorrower =
    state.includeCoBorrower && !isBorrowerEmpty(state.coBorrower)
      ? buildBorrower(state.coBorrower)
      : undefined;
  const militaryService = buildMilitary(state);
  const employment = buildEmployment(state);

  return {
    partnerReferenceId,
    propertyCity,
    propertyZipCode,
    propertyStreetAddress,
    coBorrower,
    militaryService,
    employment,
    borrower: buildBorrower(state.borrower),
    propertyState: state.propertyState.trim().toUpperCase(),
    propertyType: state.propertyType.trim(),
    estimatedValue: parseMoney(state.estimatedValue),
    loanAmount: parseMoney(state.loanAmount),
    downPayment: parseMoney(state.downPayment),
  };
}

export function buildPayload(state: ReferralFormState): BuildPayloadResult {
  if (!isLoanPurpose(state.loanPurpose)) {
    return { ok: false, error: `Unknown loan purpose: '${state.loanPurpose}'.` };
  }

  if (isFormEmpty(state)) {
    return { ok: false, error: "The form is empty. Enter referral details before submitting." };
  }

  const common = buildCommon(state);

  if (state.loanPurpose === "purchase") {
    const request: CreatePurchaseReferralRequest = {
      borrower: common.borrower,
      propertyState: common.propertyState,
      propertyType: common.propertyType,
      estimatedValue: common.estimatedValue,
      loanAmount: common.loanAmount,
    };
    request.downPayment = common.downPayment;
    if (common.partnerReferenceId) request.partnerReferenceId = common.partnerReferenceId;
    if (common.coBorrower) request.coBorrower = common.coBorrower;
    if (common.militaryService) request.militaryService = common.militaryService;
    if (common.employment) request.employment = common.employment;
    // When the buyer is still looking for a property, the street/city/ZIP are
    // intentionally omitted from the payload (they are hidden and not required).
    if (!state.propertyStillLooking) {
      if (common.propertyCity) request.propertyCity = common.propertyCity;
      if (common.propertyZipCode) request.propertyZipCode = common.propertyZipCode;
      if (common.propertyStreetAddress) request.propertyStreetAddress = common.propertyStreetAddress;
    }
    return { ok: true, purpose: "purchase", request, militaryAnswered: state.militaryAnswered };
  }

  const refinanceType = state.refinanceType.trim();
  const currentLoanBalance = parseMoney(state.currentLoanBalance);
  const request: CreateRefinanceReferralRequest = {
    borrower: common.borrower,
    propertyState: common.propertyState,
    propertyType: common.propertyType,
    currentLoanBalance,
    estimatedValue: common.estimatedValue,
    // Rate-and-term asks for no separate amount: the borrower is refinancing
    // exactly what they still owe, so the wire field is the current balance
    // itself rather than anything a prior cash-out answer may have left behind
    // (WI-TV-W65-006). Cash-out keeps the separately stated amount.
    loanAmount: refinanceType === CASH_OUT_REFINANCE ? common.loanAmount : currentLoanBalance,
    refinanceType,
  };
  if (common.partnerReferenceId) request.partnerReferenceId = common.partnerReferenceId;
  if (common.coBorrower) request.coBorrower = common.coBorrower;
  if (common.militaryService) request.militaryService = common.militaryService;
  if (common.employment) request.employment = common.employment;
  if (common.propertyCity) request.propertyCity = common.propertyCity;
  if (common.propertyZipCode) request.propertyZipCode = common.propertyZipCode;
  if (common.propertyStreetAddress) request.propertyStreetAddress = common.propertyStreetAddress;
  return { ok: true, purpose: "refinance", request, militaryAnswered: state.militaryAnswered };
}
